import { useCallback, useEffect, useRef } from 'react';
import scriptLoader from 'react-async-script-loader';
import { useDispatch } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import { Spinner, Text } from '@belong/ui';
import PropTypes from 'prop-types';
import {
  checkAchBankAccountSetupStatus,
  completeAchVerificationSession,
  createAchUpdateLinkToken,
  createAchVerificationSession,
  fetchPaymentMethods,
} from 'store/redux/user/actions';

const CHECKABLE_STATUSES = [
  'ActionRequired',
  'PendingMicrodeposit',
  'PendingUserAction',
];
const STATUS_CHECK_ATTEMPTS = 5;
const STATUS_CHECK_DELAY_MS = 1000;
const PLAID_EVENT_CALLBACK_FLUSH_DELAY_MS = 500;
const INSTITUTION_SELECTION_EVENTS = [
  'MATCHED_SELECT_INSTITUTION',
  'SELECT_INSTITUTION',
];
const PLAID_FLOW_STATES = {
  IDLE: 'idle',
  COMPLETING: 'completing',
  SETTLED: 'settled',
};

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function resolvePlaidInstitutionName(
  metadata,
  selectedInstitutionNameRef,
) {
  const institutionName = metadata?.institution?.name;

  if (institutionName || selectedInstitutionNameRef.current) {
    return institutionName ?? selectedInstitutionNameRef.current;
  }

  await wait(PLAID_EVENT_CALLBACK_FLUSH_DELAY_MS);

  return selectedInstitutionNameRef.current;
}

function validateCompletedStatus(status) {
  if (status?.status === 'Verified') {
    if (!status.accountId) {
      throw new Error(
        'JPM ACH verification returned Verified without an account ID',
      );
    }

    return;
  }

  if (!CHECKABLE_STATUSES.includes(status?.status)) {
    throw new Error(
      `JPM ACH verification ended with status ${status?.status || 'Unknown'}`,
    );
  }
}

function CreateAchJpm({
  isScriptLoaded,
  sessionId,
  onSuccess,
  onClose,
  onFail,
}) {
  const dispatch = useDispatch();
  const hasStartedRef = useRef(false);
  const flowStateRef = useRef(PLAID_FLOW_STATES.IDLE);
  const isUnmountedRef = useRef(false);
  const plaidHandlerRef = useRef(null);
  const selectedInstitutionNameRef = useRef();

  const checkStatusAfterCompletion = useCallback(
    async (status) => {
      if (
        status?.accountId ||
        !CHECKABLE_STATUSES.includes(status?.status) ||
        !status?.sessionId
      ) {
        return status;
      }

      let currentStatus = status;

      for (let attempt = 0; attempt < STATUS_CHECK_ATTEMPTS; attempt += 1) {
        if (isUnmountedRef.current) {
          return currentStatus;
        }

        if (attempt > 0) {
          await wait(STATUS_CHECK_DELAY_MS);

          if (isUnmountedRef.current) {
            return currentStatus;
          }
        }

        try {
          currentStatus = await dispatch(
            checkAchBankAccountSetupStatus(currentStatus.sessionId),
          );
        } catch (error) {
          console.error('JPM ACH status check failed: ', error);
          return currentStatus;
        }

        if (
          currentStatus?.accountId ||
          !CHECKABLE_STATUSES.includes(currentStatus?.status) ||
          !currentStatus?.sessionId
        ) {
          return currentStatus;
        }
      }

      return currentStatus;
    },
    [dispatch],
  );

  const handleFail = useCallback(
    (error) => {
      if (
        isUnmountedRef.current ||
        flowStateRef.current === PLAID_FLOW_STATES.SETTLED
      ) {
        return;
      }

      if (error) {
        console.error('JPM ACH failed: ', error);
        datadogRum.addError(error, { validation_error: 'true' });
      }

      flowStateRef.current = PLAID_FLOW_STATES.SETTLED;
      onFail();
    },
    [onFail],
  );

  const openPlaid = useCallback(async () => {
    if (!window.Plaid) {
      handleFail(new Error('Plaid Link failed to load'));
      return;
    }

    try {
      const session = sessionId
        ? await dispatch(createAchUpdateLinkToken(sessionId))
        : await dispatch(createAchVerificationSession());

      if (isUnmountedRef.current) {
        return;
      }

      plaidHandlerRef.current = window.Plaid.create({
        token: session.linkToken,
        onSuccess: async (publicToken, metadata) => {
          if (
            isUnmountedRef.current ||
            flowStateRef.current !== PLAID_FLOW_STATES.IDLE
          ) {
            return;
          }

          flowStateRef.current = PLAID_FLOW_STATES.COMPLETING;
          const account = metadata?.accounts?.[0];

          try {
            const selectedAccountId = metadata?.account_id ?? account?.id;

            if (!selectedAccountId) {
              throw new Error(
                'Plaid Link completed without a selected account ID',
              );
            }

            const institutionName = await resolvePlaidInstitutionName(
              metadata,
              selectedInstitutionNameRef,
            );
            const completedStatus = await dispatch(
              completeAchVerificationSession(session.sessionId, {
                publicToken,
                selectedAccountId,
                institutionName,
                accountType: account?.subtype,
                accountMask: account?.mask,
                verificationStatus: account?.verification_status,
                plaidLinkSessionId: metadata?.link_session_id,
              }),
            );
            const status = await checkStatusAfterCompletion(completedStatus);
            validateCompletedStatus(status);

            if (isUnmountedRef.current) {
              return;
            }

            await dispatch(fetchPaymentMethods());

            if (isUnmountedRef.current) {
              return;
            }

            flowStateRef.current = PLAID_FLOW_STATES.SETTLED;
            onSuccess(status);
          } catch (error) {
            handleFail(error);
          }
        },
        onExit: (err) => {
          if (
            isUnmountedRef.current ||
            flowStateRef.current !== PLAID_FLOW_STATES.IDLE
          ) {
            return;
          }

          if (err !== null && err !== undefined) {
            handleFail(err);
            return;
          }

          flowStateRef.current = PLAID_FLOW_STATES.SETTLED;
          onClose();
        },
        onEvent: (eventName, metadata) => {
          if (
            metadata?.institution_name &&
            (INSTITUTION_SELECTION_EVENTS.includes(eventName) ||
              !selectedInstitutionNameRef.current)
          ) {
            selectedInstitutionNameRef.current = metadata.institution_name;
          }

          if (eventName === 'ERROR') {
            datadogRum.addError(
              new Error(
                `Plaid Error ${metadata?.error_code}: ${metadata?.error_message}`,
              ),
              { validation_error: 'true' },
            );
          }
        },
      });

      plaidHandlerRef.current.open();
    } catch (error) {
      handleFail(error);
    }
  }, [
    checkStatusAfterCompletion,
    dispatch,
    handleFail,
    onClose,
    onSuccess,
    sessionId,
  ]);

  useEffect(() => {
    if (!isScriptLoaded || hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;
    openPlaid();
  }, [isScriptLoaded, openPlaid]);

  useEffect(
    () => () => {
      isUnmountedRef.current = true;
      const shouldExitPlaid =
        flowStateRef.current !== PLAID_FLOW_STATES.SETTLED;
      flowStateRef.current = PLAID_FLOW_STATES.SETTLED;

      if (plaidHandlerRef.current && shouldExitPlaid) {
        plaidHandlerRef.current.exit();
      }
    },
    [],
  );

  return (
    <div className="flex w-full flex-col items-center gap-sm py-2xl">
      <Spinner />
      <Text className="text-center">Connecting your bank...</Text>
    </div>
  );
}

CreateAchJpm.propTypes = {
  isScriptLoaded: PropTypes.bool.isRequired,
  sessionId: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onFail: PropTypes.func.isRequired,
};

CreateAchJpm.defaultProps = {
  sessionId: null,
};

export default scriptLoader([
  'https://cdn.plaid.com/link/v2/stable/link-initialize.js',
])(CreateAchJpm);
